import { Component } from '@theme/component';
import { clamp } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a draggable, clickable progress bar for a
 * slideshow-component parent. Mirrors the scroller's scroll position as a
 * filled portion of the bar and lets the user click or drag to jump.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} track - The click + drag surface.
 * @property {HTMLElement} fill - The filled portion of the bar.
 *
 * @extends {Component<Refs>}
 */
export class SlideshowProgress extends Component {
  requiredRefs = ['track', 'fill'];

  /** @type {HTMLElement | null} */
  #parent = null;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {number} */
  #columns = 1;

  /** @type {number} */
  #pageCount = 1;

  /** @type {MutationObserver | null} */
  #parentObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#parent = this.closest('slideshow-component');
    if (!this.#parent) return;

    // The parent slideshow lazy-enables scrolling via the [in-viewport]
    // attribute (see SlideshowViewportObserver in assets/slideshow.js).
    // Before that fires, scrollWidth reads 0 and the scroller is not
    // interactive, so we wait. The observer doesn't dispatch a custom event,
    // so we watch the attribute ourselves.
    const tryConnect = () => {
      if (!this.#parent?.hasAttribute('in-viewport')) return;
      this.#scroller = this.#parent.querySelector('[ref="scroller"]');
      if (!this.#scroller) return;
      this.#updateGeometry();
      this.#updateFill();
      this.#bind();
      if (this.#parentObserver) {
        this.#parentObserver.disconnect();
        this.#parentObserver = null;
      }

      // The slideshow's initial scroll position is set inside a
      // requestAnimationFrame, and its IntersectionObserver fires
      // asynchronously. Re-measure on the next frame so the bar reflects
      // the real layout (and unhides itself if the first measurement
      // caught a transient zero-width state).
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#updateGeometry();
        this.#updateFill();
      });
    };

    this.#parentObserver = new MutationObserver(tryConnect);
    this.#parentObserver.observe(this.#parent, {
      attributes: true,
      attributeFilter: ['in-viewport'],
    });

    tryConnect();
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbind();
    this.#parentObserver?.disconnect();
    this.#parentObserver = null;
    window.removeEventListener('resize', this.#onResize);
  }

  #bind() {
    if (!this.#scroller) return;
    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#parent?.addEventListener(SlideshowSelectEvent.eventName, this.#onSelect);
    this.refs.track.addEventListener('pointerdown', this.#onPointerDown);
    this.refs.track.addEventListener('keydown', this.#onKeyDown);
  }

  #unbind() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#parent?.removeEventListener(SlideshowSelectEvent.eventName, this.#onSelect);
    this.refs.track?.removeEventListener('pointerdown', this.#onPointerDown);
    this.refs.track?.removeEventListener('keydown', this.#onKeyDown);
  }

  #onResize = () => {
    this.#updateGeometry();
    this.#updateFill();
  };

  #onScroll = () => this.#updateFill();

  #onSelect = () => {
    this.#updateGeometry();
    this.#updateFill();
  };

  #onPointerDown = (event) => {
    if (!this.#scroller || !this.#parent) return;
    if (event.button !== undefined && event.button !== 0) return;

    event.preventDefault();
    this.refs.track.setPointerCapture(event.pointerId);

    this.#parent.setAttribute('dragging', '');

    // Suspend the scroller's snap so the drag scrubs smoothly, mirroring
    // Scroller.snap=false (see assets/scrolling.js:261).
    this.#scroller.style.scrollSnapType = 'none';

    this.#scrubTo(event);

    this.refs.track.addEventListener('pointermove', this.#onPointerMove);
    this.refs.track.addEventListener('pointerup', this.#onPointerEnd);
    this.refs.track.addEventListener('pointercancel', this.#onPointerEnd);
  };

  #onPointerMove = (event) => {
    this.#scrubTo(event);
  };

  #onPointerEnd = (event) => {
    if (!this.#scroller || !this.#parent) return;

    this.refs.track.removeEventListener('pointermove', this.#onPointerMove);
    this.refs.track.removeEventListener('pointerup', this.#onPointerEnd);
    this.refs.track.removeEventListener('pointercancel', this.#onPointerEnd);

    // Restore the scroller's snap (clears the inline override).
    this.#scroller.style.scrollSnapType = '';

    this.#parent.removeAttribute('dragging');

    try {
      this.refs.track.releasePointerCapture(event.pointerId);
    } catch (_) {
      // Pointer was already released.
    }

    // Snap to the nearest page on release.
    const page = this.#pageFromEvent(event);
    this.#parent.select(page * this.#columns, event, { animate: true });
  };

  #onKeyDown = (event) => {
    if (!this.#parent) return;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        this.#parent.previous(event);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        this.#parent.next(event);
        break;
      case 'Home':
        event.preventDefault();
        this.#parent.select(0, event, { animate: true });
        break;
      case 'End': {
        event.preventDefault();
        const lastIndex = (this.#parent.slides?.length || 1) - 1;
        this.#parent.select(lastIndex, event, { animate: true });
        break;
      }
    }
  };

  #scrubTo(event) {
    if (!this.#scroller) return;
    const rect = this.refs.track.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const maxScroll = this.#scroller.scrollWidth - this.#scroller.clientWidth;
    if (maxScroll <= 0) return;
    this.#scroller.scrollLeft = ratio * maxScroll;
  }

  #pageFromEvent(event) {
    if (!this.refs.track) return 0;
    const rect = this.refs.track.getBoundingClientRect();
    if (!rect.width || this.#pageCount <= 1) return 0;
    const ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    return Math.round(ratio * (this.#pageCount - 1));
  }

  /**
   * Derive the column count from the layout (one column = one slide's width
   * fits inside the visible viewport) and recompute the page count.
   */
  #updateGeometry() {
    if (!this.#scroller || !this.#parent) return;
    const slideCount = this.#parent.slides?.length || 0;
    if (!slideCount) {
      this.#columns = 1;
      this.#pageCount = 1;
      this.toggleAttribute('hidden', true);
      return;
    }

    const viewport = this.#scroller.clientWidth;
    const firstSlide = this.#parent.slides?.[0];
    const slideWidth = firstSlide ? firstSlide.getBoundingClientRect().width : viewport;

    if (slideWidth > 0 && viewport > 0) {
      this.#columns = Math.max(1, Math.round(viewport / slideWidth));
    } else {
      this.#columns = 1;
    }
    this.#pageCount = Math.max(1, Math.ceil(slideCount / this.#columns));
    this.toggleAttribute('hidden', this.#pageCount <= 1);
  }

  #updateFill() {
    if (!this.#scroller || !this.refs.fill) return;
    const max = this.#scroller.scrollWidth - this.#scroller.clientWidth;
    const scrollPct = max > 0 ? (this.#scroller.scrollLeft / max) * 100 : 0;
    // Always show at least one page's worth of fill so the bar reads as a
    // progress indicator from the very first render (otherwise it looks
    // empty until the user scrolls).
    const minPct = this.#pageCount > 1 ? 100 / this.#pageCount : 0;
    const pct = Math.max(scrollPct, minPct);
    this.refs.fill.style.width = `${pct}%`;
  }
}

if (!customElements.get('slideshow-progress')) {
  customElements.define('slideshow-progress', SlideshowProgress);
}
